import { useEffect, useId, useState, type FormEvent } from 'react';
import { Building2 } from 'lucide-react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksPhotoUpload } from '../../components/liks/LiksPhotoUpload/LiksPhotoUpload';
import { LiksTextFormField } from '../../components/liks/LiksTextFormField/LiksTextFormField';
import { useUpdateTenantProfileMutation } from '../../features/tenant/mutations/useTenantProfileMutations';
import { useI18n } from '../../i18n/useI18n';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import type { Tenant } from '../../types/tenant';
import {
  TENANT_LOGO_MAX_BYTES,
  logoToFormValue,
  readFileAsDataUrl,
  toPhotoDisplaySrc,
  toPhotoPayload,
} from '../../utils/imageUtils';

const DESCRIPTION_MAX_LENGTH = 2000;

function initialDescription(tenant: Tenant): string {
  return tenant.description ?? '';
}

type TenantDetailsFormProps = {
  tenant: Tenant;
};

export function TenantDetailsForm({ tenant }: TenantDetailsFormProps) {
  const { translate } = useI18n();
  const updateMutation = useUpdateTenantProfileMutation();

  const logoFieldId = useId();

  const [description, setDescription] = useState(() =>
    initialDescription(tenant),
  );
  const [logo, setLogo] = useState<string | null>(() =>
    logoToFormValue(tenant.logo),
  );
  const [logoTouched, setLogoTouched] = useState(false);
  const [isLogoHydrating, setIsLogoHydrating] = useState(
    () =>
      logo == null &&
      typeof Blob !== 'undefined' &&
      tenant.logo instanceof Blob &&
      tenant.logo.size > 0,
  );
  const [logoError, setLogoError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const hydrateLogoFromBlob = async () => {
      if (logo != null) {
        setIsLogoHydrating(false);
        return;
      }
      if (typeof Blob === 'undefined' || !(tenant.logo instanceof Blob)) {
        setIsLogoHydrating(false);
        return;
      }
      if (tenant.logo.size === 0) {
        setIsLogoHydrating(false);
        return;
      }

      try {
        const dataUrl = await readFileAsDataUrl(
          new File([tenant.logo], 'logo', {
            type: tenant.logo.type || 'image/png',
          }),
        );
        if (!cancelled) {
          setLogo(toPhotoPayload(dataUrl));
          setIsLogoHydrating(false);
        }
      } catch {
        if (!cancelled) {
          setIsLogoHydrating(false);
        }
      }
    };

    void hydrateLogoFromBlob();

    return () => {
      cancelled = true;
    };
  }, [logo, tenant.logo]);

  const isSubmitting = updateMutation.isPending;
  const trimmedDescription = description.trim();
  const isDescriptionTooLong =
    trimmedDescription.length > DESCRIPTION_MAX_LENGTH;

  const isDescriptionDirty =
    trimmedDescription !== initialDescription(tenant).trim();
  const isDirty = isDescriptionDirty || logoTouched;

  const isSaveDisabled =
    !isDirty ||
    isSubmitting ||
    isLogoHydrating ||
    isDescriptionTooLong ||
    logoError != null;

  const handleLogoChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setLogoError(
        translate('organisationManagement.organisation.logoInvalidType'),
      );
      return;
    }

    if (file.size > TENANT_LOGO_MAX_BYTES) {
      setLogoError(
        translate('organisationManagement.organisation.logoTooLarge'),
      );
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setLogo(toPhotoPayload(dataUrl));
      setLogoTouched(true);
      setLogoError(null);
    } catch {
      setLogoError(
        translate('organisationManagement.organisation.logoReadError'),
      );
    }
  };

  const handleRemoveLogo = () => {
    setLogo(null);
    setLogoTouched(true);
    setLogoError(null);
  };

  const handleCancel = () => {
    setDescription(initialDescription(tenant));
    setLogo(logoToFormValue(tenant.logo));
    setLogoTouched(false);
    setLogoError(null);
    setIsLogoHydrating(
      typeof Blob !== 'undefined' &&
        tenant.logo instanceof Blob &&
        tenant.logo.size > 0 &&
        logoToFormValue(tenant.logo) == null,
    );
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaveDisabled) {
      return;
    }

    updateMutation.mutate(
      {
        current: tenant,
        description: trimmedDescription,
        ...(logoTouched ? { logo: toPhotoPayload(logo) } : {}),
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate(
              'organisationManagement.organisation.saveSuccessTitle',
            ),
            description: translate(
              'organisationManagement.organisation.saveSuccessDescription',
            ),
          });
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('organisationManagement.organisation.saveErrorTitle'),
            translate(
              'organisationManagement.organisation.saveErrorDescription',
            ),
          );
        },
      },
    );
  };

  const logoDisplaySrc = toPhotoDisplaySrc(logo);

  return (
    <form className="tenant-tab__form" onSubmit={handleSubmit}>
      <div className="tenant-tab__card">
        <div className="tenant-tab__body">
          <LiksTextFormField
            label={
              <>
                {translate('organisationManagement.organisation.nameLabel')}
                <span className="tenant-tab__label-hint">
                  {translate(
                    'organisationManagement.organisation.nameReadOnlyHint',
                  )}
                </span>
              </>
            }
            value={tenant.name}
            disabled
            readOnly
          />

          <LiksPhotoUpload
            className="tenant-tab__logo"
            variant="tenant"
            label={translate('organisationManagement.organisation.logoLabel')}
            src={logoDisplaySrc}
            isLoading={isLogoHydrating}
            placeholder={<Building2 strokeWidth={1.5} aria-hidden />}
            inputId={logoFieldId}
            disabled={isSubmitting}
            onFileChange={(event) => {
              void handleLogoChange(event);
            }}
            onRemove={handleRemoveLogo}
            error={logoError}
          />

          <LiksTextFormField
            label={translate(
              'organisationManagement.organisation.descriptionLabel',
            )}
            multiline
            value={description}
            onChange={setDescription}
            disabled={isSubmitting}
            autoComplete="off"
            error={
              isDescriptionTooLong
                ? translate(
                    'organisationManagement.organisation.descriptionTooLong',
                  )
                : null
            }
          />
        </div>

        <div className="tenant-tab__actions">
          {isDirty ? (
            <LiksButton
              type="button"
              variant="secondary"
              disabled={isSubmitting}
              onClick={handleCancel}
            >
              {translate('organisationManagement.organisation.cancel')}
            </LiksButton>
          ) : null}
          <LiksButton type="submit" disabled={isSaveDisabled}>
            {translate('organisationManagement.organisation.save')}
          </LiksButton>
        </div>
      </div>
    </form>
  );
}
